import React, { useEffect, useState } from 'react'
import { graphql, useStaticQuery, Link } from 'gatsby'
import { useLocation } from '@reach/router'
import { FiArrowDownRight, FiMenu, FiX } from 'react-icons/fi'
import { useLanguage } from '../../context/LanguageContext'
import LanguageSwitcher from './LanguageSwitcher'
import { NOM_NOM_SLUG, getHomePath } from '../../utils/paths'
import logoDefault from '../../images/xpantia-logo.png'
import logoBlack from '../../images/xpantia-logo-black.png'

// Menu pages per language, in display order. The second entry (products) gets the
// Nom-Nom dropdown.
const MENU_SLUGS = {
  EL: ['home', 'our_products', 'company', 'news'],
  EN: ['homepage', 'products', 'our-team', 'blog'],
}
const HOME_SLUGS = ['home', 'homepage']
const CONTACT_SLUGS = { EL: 'contact', EN: 'contactus' }

// Bar height per breakpoint (logo + padding, plus the bottom border below lg), fixed so
// NavSpace leaves exactly the right room for it.
const BAR_HEIGHT = 'h-[57px] sm:h-[65px] lg:h-16'

// `bar` is the bar's background; `scrolledBar` the same on large screens once the page has
// scrolled, where a full-width backdrop takes over the background.
const THEMES = {
  default: {
    logo: logoDefault,
    bar: 'bg-white lg:bg-paper/75',
    scrolledBar: 'bg-white lg:bg-transparent',
    border: 'border-silver',
    link: 'hover:text-brand',
    activeLink: 'nav-item-selected hover:text-paper',
    contact: 'border-brand hover:bg-brand hover:text-paper/75',
    icon: 'text-brand',
  },
  products: {
    logo: logoBlack,
    bar: 'bg-nomnom-mint',
    scrolledBar: 'bg-nomnom-mint lg:bg-transparent',
    border: 'border-nomnom-ink',
    link: 'hover:text-nomnom-forest',
    activeLink: 'nav-item-selected hover:text-white hover:bg-nomnom-forest',
    contact: 'border-black hover:bg-black hover:text-nomnom-mint',
    icon: 'text-nomnom-ink',
  },
}

// Rendered once by Layout, which keeps it stuck to the top of the window. On large screens
// it floats as a rounded bar at the top of the page and eases into a full-width bar once the
// page scrolls. Only transforms, colours and opacity animate, so nothing on the page shifts.
const Nav = ({ variant = 'default', alternates }) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { lang } = useLanguage()
  const { pathname } = useLocation()
  const theme = THEMES[variant]

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0)
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // The nav stays mounted from page to page, so close the menu after navigating.
  useEffect(() => {
    setIsMenuOpen(false)
  }, [pathname])

  const data = useStaticQuery(graphql`
    query {
      allWpPage(sort: { slug: ASC }) {
        nodes {
          id
          slug
          uri
          title
          language {
            code
          }
        }
      }
    }
  `)

  const pages = data.allWpPage.nodes
  const findPage = (slug) =>
    pages.find((page) => page.slug === slug && page.language?.code === lang)

  const homePath = getHomePath(lang)
  const menuSlugs = MENU_SLUGS[lang] || []
  const productsSlug = menuSlugs[1]
  const contactPage = findPage(CONTACT_SLUGS[lang])
  const nomNomPage = findPage(NOM_NOM_SLUG)

  const navItems = menuSlugs
    .map(findPage)
    .filter(Boolean)
    .map((page) => ({
      label: page.title,
      path: HOME_SLUGS.includes(page.slug) ? homePath : page.uri,
      slug: page.slug,
    }))

  const currentPath = pathname.endsWith('/') ? pathname : `${pathname}/`

  // Desktop items also stay active on pages below them (except Home); mobile links
  // only on an exact match.
  const isCurrent = (path) => currentPath === path
  const isItemActive = (path) =>
    path === homePath ? isCurrent(path) : currentPath.startsWith(path)

  const closeMenu = () => setIsMenuOpen(false)
  const mobileLinkClass = (active, idle = theme.link) =>
    active ? 'nav-item nav-item-selected hover:text-paper' : `nav-item ${idle}`

  return (
    <div className="relative">
      {/* Full-width backdrop of the scrolled bar (large screens) */}
      <div
        aria-hidden="true"
        className={`hidden lg:block absolute inset-x-0 top-0 ${BAR_HEIGHT} ${theme.bar} shadow-nav transition-opacity duration-300 ease-out ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* No transform below lg: it would trap the full-screen mobile menu inside the bar. */}
      <div
        className={`container transition-transform duration-300 ease-out ${
          isScrolled ? '' : 'lg:translate-y-6'
        }`}
      >
        <nav
          className={`${BAR_HEIGHT} relative w-full border-b lg:border-none ${theme.border} ${
            isScrolled
              ? `${theme.scrolledBar} sm:shadow-nav lg:shadow-none`
              : `${theme.bar} sm:shadow-nav lg:rounded-[33px]`
          } transition-[background-color,box-shadow,border-radius] duration-300 ease-out py-2 px-4 sm:px-6 flex items-center justify-between lg:whitespace-nowrap`}
        >
          <div className="flex items-center shrink-0">
            <Link to={homePath}>
              <img src={theme.logo} alt="Xpantia Logo" className="h-10 sm:h-12 sm:block" />
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center space-x-8 text-lg">
            <div className="hidden xl:block w-[85px] h-px bg-black rounded" />

            {navItems.map((item) => {
              const linkClass = `nav-item flex items-center ${
                isItemActive(item.path) ? theme.activeLink : theme.link
              }`

              if (item.slug !== productsSlug) {
                return (
                  <Link key={item.path} to={item.path} className={linkClass}>
                    <div className="flex items-center gap-2 md:gap-3 lg:gap-4">{item.label}</div>
                  </Link>
                )
              }

              return (
                <div key={item.path} className="relative group">
                  <Link to={item.path} className={linkClass}>
                    <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
                      {item.label}
                      <FiArrowDownRight className="text-base md:text-lg" />
                    </div>
                  </Link>

                  {/* Hover bridge: keeps the dropdown open while the pointer moves onto it */}
                  <div className="absolute left-0 top-full h-3 w-full" />

                  {nomNomPage && (
                    <div
                      className="absolute left-0 top-full mt-2 hidden group-hover:block group-focus-within:block bg-white/95 backdrop-blur-sm shadow-lg border border-gray-200 rounded-xl overflow-hidden min-w-[220px] z-50 transition-all duration-200 ease-out"
                      tabIndex={-1}
                    >
                      <Link
                        to={nomNomPage.uri}
                        className="block px-5 py-3 text-sm font-medium text-gray-800 hover:bg-paper transition-colors duration-150"
                      >
                        {nomNomPage.title}
                      </Link>
                    </div>
                  )}
                </div>
              )
            })}

            <div className="hidden xl:block w-[85px] h-px bg-black rounded" />

            <div className="ml-4">
              <LanguageSwitcher alternates={alternates} />
            </div>
          </div>

          {/* Contact button */}
          {contactPage && (
            <div className="hidden lg:block relative rounded-full">
              <Link
                to={contactPage.uri}
                className={`nav-item flex items-center justify-center px-4 py-2 rounded-full border-2 ${theme.contact}`}
              >
                {contactPage.title}
              </Link>
            </div>
          )}

          {/* Burger menu */}
          <div className="block lg:hidden z-50">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <FiX size={28} className={theme.icon} />
              ) : (
                <FiMenu size={28} className={theme.icon} />
              )}
            </button>
          </div>

          {/* Mobile menu */}
          {isMenuOpen && (
            <div className="fixed inset-0 bg-paper z-40 flex flex-col items-center justify-center space-y-6 text-xl px-6">
              {navItems.map((item) => (
                <React.Fragment key={item.path}>
                  <Link
                    to={item.path}
                    className={mobileLinkClass(isCurrent(item.path))}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </Link>

                  {item.slug === productsSlug && nomNomPage && (
                    <Link
                      to={nomNomPage.uri}
                      className={
                        isCurrent(nomNomPage.uri)
                          ? 'nav-item-sub nav-item-sub-selected hover:text-paper'
                          : `nav-item-sub ${theme.link}`
                      }
                      onClick={closeMenu}
                    >
                      {nomNomPage.title}
                    </Link>
                  )}
                </React.Fragment>
              ))}

              {contactPage && (
                <Link
                  to={contactPage.uri}
                  className={mobileLinkClass(isCurrent(contactPage.uri), 'hover:text-brand')}
                  onClick={closeMenu}
                >
                  {contactPage.title}
                </Link>
              )}

              <div className="flex gap-4 text-2xl">
                <LanguageSwitcher alternates={alternates} />
              </div>
            </div>
          )}
        </nav>
      </div>
    </div>
  )
}

/** Room at the top of a page for the nav, which floats over it (see Layout). */
export const NavSpace = () => (
  <div aria-hidden="true" className="lg:pt-6">
    <div className={BAR_HEIGHT} />
  </div>
)

export default Nav
